import { useCallback } from 'react';

import { useQueryClient } from '@tanstack/react-query';
import { useSnackbar } from 'notistack';

import useAppNavigation from '~/hooks/useAppNavigation';

import type { Attraction } from '~/types/attractionTypes';

import {
  getCanonicalAttractionResolution,
  getReadResolvedAttractionId,
  retargetAttractionCache,
} from './attractionCanonicalization';
import { attractionQueryKeys } from './attractionQueryKeys';

type RetargetOptions = {
  attraction?: Attraction | null;
  redirect?: boolean;
};

export function useCanonicalAttractionRedirect() {
  const queryClient = useQueryClient();
  const { replace } = useAppNavigation();
  const { enqueueSnackbar } = useSnackbar();

  const retarget = useCallback(
    (fromAttractionId: string, toAttractionId: string, options: RetargetOptions = {}) => {
      retargetAttractionCache({
        queryClient,
        fromAttractionId,
        toAttractionId,
        attraction: options.attraction,
      });

      if (options.redirect !== false) {
        replace(`/attractions/${toAttractionId}`);
      }

      enqueueSnackbar(
        options.redirect === false
          ? 'Attraction was canonicalized. Refreshing attractions.'
          : 'Attraction was canonicalized. Redirected to the canonical attraction.',
        { variant: 'info' },
      );
    },
    [enqueueSnackbar, queryClient, replace],
  );

  const retargetFromRead = useCallback(
    (requestedAttractionId: string, attraction: Attraction | null | undefined) => {
      const resolvedId = getReadResolvedAttractionId(attraction, requestedAttractionId);
      if (!resolvedId) return false;

      retarget(requestedAttractionId, resolvedId, { attraction });
      return true;
    },
    [retarget],
  );

  const retargetFromMutationError = useCallback(
    (error: unknown, fallbackLoserAttractionId?: string, options: { redirect?: boolean } = {}) => {
      const resolution = getCanonicalAttractionResolution(error);
      if (!resolution) return false;

      const loserAttractionId = resolution.loserAttractionId || fallbackLoserAttractionId;
      if (!loserAttractionId) {
        queryClient.invalidateQueries({ queryKey: attractionQueryKeys.lists() });
        enqueueSnackbar('Attraction was canonicalized. Refreshing attractions.', { variant: 'info' });
        return true;
      }

      retarget(loserAttractionId, resolution.canonicalAttractionId, options);
      return true;
    },
    [enqueueSnackbar, queryClient, retarget],
  );

  return { retargetFromMutationError, retargetFromRead };
}
